import { acquireFailureKey, transportFailureKey } from './requestFailure';
import { referenceViolationDescriptor } from './referenceViolation';
import { getIntl } from '@umijs/max';
import { getErrorMessage, isKnownAuthErrorCode } from '@/services/auth/errorCodes';
import {
  getDramaErrorMessage,
  isDramaErrorCode,
} from '@/services/drama/errorCodes';

const RAW_HTTP_STATUS_MESSAGE_RE = /^Request failed with status code\s+(\d+)$/i;

const HTTP_STATUS_MESSAGE_KEYS: Record<number, string> = {
  400: 'requestError.http.400',
  401: 'requestError.unauthorized',
  413: 'requestError.http.413',
  415: 'requestError.http.415',
  422: 'requestError.http.422',
  403: 'requestError.http.403',
  404: 'requestError.http.404',
  409: 'requestError.http.409',
  423: 'requestError.http.423',
  429: 'requestError.http.429',
  500: 'requestError.http.500',
  502: 'requestError.http.502',
  503: 'requestError.http.503',
  504: 'requestError.http.504',
};

const TECHNICAL_MESSAGE_PATTERNS: RegExp[] = [
  RAW_HTTP_STATUS_MESSAGE_RE,
  /\b(ffmpeg|ffprobe|syntaxerror|typeerror|referenceerror|unexpected token|unexpected end)\b/i,
  /\b(stack trace|panic|goroutine|runtime error|exception)\b/i,
  /\b(sql|mysql|postgres|mongodb|mongo|redis|bson|objectid)\b/i,
  /\b(econnreset|econnrefused|enotfound|etimedout|socket hang up|tls handshake)\b/i,
  /\b(upstream|provider|adapter|submit|poll|webhook|endpoint|status code)\b/i,
  /\b(authorization|bearer|api[_-]?key|secret|token)\b/i,
  /https?:\/\/|\b[a-z0-9.-]+:\d{2,5}\b/i,
  /\/[^\s]+\.(go|ts|tsx|js|jsx)(:\d+)?/i,
];

function t(id: string, values?: Record<string, string | number>): string {
  return getIntl().formatMessage({ id }, values);
}

export function parseStatusFromErrorMessage(
  message: unknown,
): number | undefined {
  if (typeof message !== 'string') return undefined;
  const match = message.trim().match(RAW_HTTP_STATUS_MESSAGE_RE);
  if (!match) return undefined;
  const status = Number(match[1]);
  return Number.isFinite(status) ? status : undefined;
}

export function resolveHttpStatusUserMessage(
  status: number | undefined,
): string {
  if (!status) return '';
  const key = HTTP_STATUS_MESSAGE_KEYS[status];
  if (key) return t(key);
  if (status >= 500) return t('requestError.http.5xx');
  if (status >= 400) return t('requestError.http.4xx');
  return '';
}

export function resolveKnownErrorCodeMessage(
  code: string | undefined | null,
): string {
  if (!code) return '';
  if (isDramaErrorCode(code)) return getDramaErrorMessage(code);
  if (isKnownAuthErrorCode(code)) return getErrorMessage(code);
  return '';
}

export function isSafeUserFacingMessage(message: unknown): message is string {
  if (typeof message !== 'string') return false;
  const text = message.trim();
  if (!text || text.length > 240 || /^[DE]\d{3}$/i.test(text)) return false;
  if (/^\s*(<!doctype|<html\b|<head\b|<body\b)/i.test(text)) return false;
  if (/^(?:\{|\[)/.test(text)) {
    try { const parsed = JSON.parse(text); if (parsed && typeof parsed === 'object') return false; } catch {}
  }
  if (/^(未知错误|unknown error|error|操作失败|请求失败|internal server error|bad gateway|gateway timeout|service unavailable|request failed|network error|failed to fetch|load failed)$/i.test(text)) return false;
  return !TECHNICAL_MESSAGE_PATTERNS.some((pattern) => pattern.test(text));
}

export function safeBackendUserMessage(message: unknown): string {
  return isSafeUserFacingMessage(message) ? message.trim() : '';
}

function pickString(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

function extractData(input: any): any {
  if (typeof input === 'string') return { message: input };
  return input?.response?.data ?? input?.info?.data ?? input;
}

export function extractErrorCode(input: any): string | undefined {
  const data = extractData(input);
  return (
    pickString(
      data?.error?.code,
      data?.error_code,
      data?.errorCode,
      /^[DE]\d{3}$/i.test(data?.message || '') ? data.message : undefined,
      data?.code,
      input?.info?.errorCode,
    ) || undefined
  );
}

export function getUserErrorMessage(input: any, fallback?: string): string {
  const data = extractData(input);
  const err = data?.error ?? {};
  const status: number | undefined =
    input?.response?.status ??
    data?.status ??
    parseStatusFromErrorMessage(input?.message);
  const code = extractErrorCode(input);

  const codeMessage = resolveKnownErrorCodeMessage(code);
  const acquireKey = acquireFailureKey(data);
  const transportKey = transportFailureKey(typeof input === 'string' ? {message:input} : input);
  const fields = err?.fields ?? err?.details?.fields;
  const fieldMessage = fields && typeof fields === 'object'
    ? Object.values(fields).map(safeBackendUserMessage).filter(Boolean).slice(0, 2).join('; ')
    : '';

  if (code === 'E092') {
    const wait = err?.details?.waitSeconds;
    if (typeof wait === 'number' && Number.isFinite(wait) && wait > 0) {
      return t('requestError.rateLimitWait', { wait: Math.ceil(wait) });
    }
  }

  return (
    (code === 'D214' ? referenceViolationMessage(err?.details) : '') ||
    safeBackendUserMessage(
      pickString(
        err?.user_message,
        err?.userMessage,
        data?.user_message,
        data?.userMessage,
      ),
    ) ||
    fieldMessage ||
    (acquireKey ? t(acquireKey) : '') ||
    codeMessage ||
    (transportKey ? t(transportKey) : '') ||
    safeBackendUserMessage(data?.message) ||
    safeBackendUserMessage(err?.message) ||
    safeBackendUserMessage(input?.friendlyMessage) ||
    safeBackendUserMessage(input?.info?.errorMessage) ||
    resolveHttpStatusUserMessage(status) ||
    (input?.request ? t('requestError.network') : '') ||
    safeBackendUserMessage(fallback) ||
    t('requestError.fallback')
  );
}

export function getThrowableUserErrorMessage(
  error: unknown,
  fallback?: string,
): string {
  if (error instanceof Error) return getUserErrorMessage(error, fallback);
  return getUserErrorMessage(error, fallback);
}

// Store the approved message separately from the machine code so the node can show it after the toast closes.
export function serializeUserFacingError(input: unknown, fallback?: string): string {
  return JSON.stringify({ code: extractErrorCode(input), user_message: getUserErrorMessage(input, fallback) });
}

export function storedUserErrorMessage(raw: unknown): string {
  if (typeof raw !== 'string' || !raw.trim().startsWith('{')) return '';
  try {
    const parsed = JSON.parse(raw);
    return safeBackendUserMessage(parsed?.user_message);
  } catch { return ''; }
}

export function storedUserErrorCode(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined;
  try { return extractErrorCode(JSON.parse(raw)); } catch { return undefined; }
}

export function referenceViolationMessage(details: unknown): string {
  const descriptor = referenceViolationDescriptor(details);
  return descriptor ? t(descriptor.id, descriptor.values) : '';
}
